import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Role } from '@maktech/shared';
import { setAccessToken } from '@/lib/api/client';
import * as authApi from '@/api/auth.api';
import { setAdminDemoActor, setAdminDemoRole } from '@/pages/admin/demo/role/adminDemoRole';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  avatarInitials: string | null;
  points: number;
  streak: number;
}

interface AuthState {
  user: AuthUser | null;
  /** True until the initial silent refresh settles. Guards must WAIT on this. */
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  signup: (name: string, email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * On mount, try a silent refresh. The access token is memory-only, so a page
   * reload always starts logged-out until this resolves — which is exactly why
   * `loading` exists. Rendering a guest view before this settles would flash
   * "Log in" at an authenticated user on every refresh.
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authApi.refreshSession();
        if (cancelled) return;
        setAccessToken(r.accessToken);
        setAdminDemoRole(r.user.role);
        setAdminDemoActor({ email: r.user.email, role: r.user.role });
        setUser(r.user);
      } catch {
        if (!cancelled) {
          setAccessToken(null);
          setAdminDemoRole(null);
          setAdminDemoActor(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setAdminDemoRole(user?.role ?? null);
    setAdminDemoActor(
      user ? { email: user.email, role: user.role } : null,
    );
  }, [user]);

  const login = useCallback(async (email: string, password: string) => {
    const r = await authApi.login(email, password);
    setAccessToken(r.accessToken);
    setAdminDemoRole(r.user.role);
    setAdminDemoActor({ email: r.user.email, role: r.user.role });
    setUser(r.user);
    return r.user;
  }, []);

  const signup = useCallback(async (name: string, email: string, password: string) => {
    const r = await authApi.signup(name, email, password);
    setAccessToken(r.accessToken);
    setAdminDemoRole(r.user.role);
    setAdminDemoActor({ email: r.user.email, role: r.user.role });
    setUser(r.user);
    return r.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setAccessToken(null);
      setAdminDemoRole(null);
      setAdminDemoActor(null);
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, signup, logout }),
    [user, loading, login, signup, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}
